/**
 * complianceRoles — pure helpers for the multi-role console.
 *
 * A user can now hold several roles at once (AD, UDP, CCO, DR, BDC,
 * CONSULTANT, EMPLOYEE). The compliance roles form an inheritance hierarchy
 * UDP > CCO > DR:
 *   canDecide — may decide KYC / accreditation / classification / suitability
 *               (holds any of DR, CCO, UDP)
 *   canReview — may review another reviewer's decision (holds CCO or UDP)
 *
 * AD ("Platform Manager") is oversight only: it reassigns, escalates and reads,
 * but it does not decide. An AD who also holds CCO decides through the CCO
 * role, not through AD.
 *
 * This module is deliberately dependency-free so it can be unit tested and
 * reused by hooks, guards and navigation alike.
 */

/** Role codes in precedence order; the first match is the primary role. */
export const RANK = ["AD", "UDP", "CCO", "DR", "BDC", "CONSULTANT", "EMPLOYEE"];

/** Unknown codes rank after every known one rather than before them. */
const rankOf = (role) => {
  const i = RANK.indexOf(role);
  return i === -1 ? RANK.length : i;
};

/**
 * normalizeRoles — read the role list off a login payload, tolerating the two
 * older single-role shapes so a cached pre-multi-role session still resolves.
 */
export function normalizeRoles(src) {
  if (!src) return [];
  if (Array.isArray(src.roles) && src.roles.length > 0) return src.roles;
  if (src.role_name) return [src.role_name];
  if (src.AdminRole && src.AdminRole.role) return [src.AdminRole.role];
  return [];
}

/** primaryRole — the highest-ranked code held, or null when none is held. */
export function primaryRole(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  return [...roles].sort((a, b) => rankOf(a) - rankOf(b))[0];
}

/**
 * hasAnyRole — plain set intersection. There is NO Super Admin wildcard here
 * on purpose: the legacy wildcard is an authentication-era bypass and lives in
 * useRole, so the compliance predicates below can never be widened by it.
 */
export function hasAnyRole(roles, ...names) {
  if (!Array.isArray(roles)) return false;
  return names.some((name) => roles.includes(name));
}

/** sortRoles — a copy of the list in RANK order (unknown codes last). */
export function sortRoles(roles) {
  if (!Array.isArray(roles)) return [];
  return [...roles].sort((a, b) => rankOf(a) - rankOf(b));
}

/**
 * deriveCompliance — the four predicates the console gates on.
 * Super Admin is a legacy all-admin role: it never confers a compliance
 * authority, because deciding and reviewing are regulated capacities that
 * have to be granted explicitly.
 */
/**
 * Legacy 'Super Admin' arrives spelled several ways: the canonical rows carry
 * 'Super Admin', while the API upper-cases legacy role names in dark mode, so a
 * real session shows up holding 'SUPER ADMIN'. An exact-match check dropped the
 * wildcard for exactly those sessions and took their whole nav with it.
 */
const isSuperAdminCode = (code) =>
  String(code || "").trim().toUpperCase() === "SUPER ADMIN";

export function deriveCompliance(roles) {
  const list = Array.isArray(roles) ? roles : [];
  const canDecide = hasAnyRole(list, "DR", "CCO", "UDP");
  const canReview = hasAnyRole(list, "CCO", "UDP");
  const isAD = list.includes("AD");
  return {
    canDecide,
    canReview,
    isAD,
    isOversight: isAD && !canDecide,
    isSuperAdmin: list.some(isSuperAdminCode),
  };
}
